import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { guid } from '@/mixins/guid';
import linkPage from '@editor/droplets/_bubble-options/components/link-page';
import { colShape } from '../../utils';

export default Vue.component('edit-columns', {
  template: require('./template.html'),
  mixins: [guid],
  components: {
    linkPage,
  },
  computed: {
    ...mapGetters(['viewport']),
    setClass() {
      const cols = this.droplet.columns.length;
      let cls = `col-${12 / cols}`;
      cls = cls.replace(/\./g, '-');
      return cls;
    },
    minCount() {
      const total = this.droplet.columns.length;
      let disableClass = false;
      if (total <= this.minLimit) {
        disableClass = true;
      }
      return disableClass;
    },
    maxCount() {
      const total = this.droplet.columns.length;
      let disableClass = false;
      if (total >= 6) {
        disableClass = true;
      }
      return disableClass;
    },
  },
  data() {
    return {
      startX: 0,
      col: null,
      colW: null,
      nxtCol: null,
      nxtColW: null,
      minLimit: 1,
      activeLink: null,
      activeLinkColIndex: 0,
    };
  },
  name: 'edit-columns',
  props: ['droplet', 'show-edit-bubble'],
  watch: {
    showEditBubble(open) {
      if (!open) {
        this.activeLink = null;
      }
    },
    activeLink(column) {
      this.$emit('set-active-link', column);
    },
  },
  methods: {
    getWidth(i) {
      let w = this.droplet.columns[i].mediaQueries[0].desktop;
      if (i === this.col) {
        w = `${this.colW}%`;
      } else if (i === this.nxtCol) {
        w = `${this.nxtColW}%`;
      } else if (w === 'default') {
        w = '';
      } else {
        w = `${w}%`;
      }
      return w;
    },
    setEqHeights(e) {
      const equalHeights = e.currentTarget.checked;
      const payload = {};
      payload.drp = this.droplet;
      payload.equalHeights = equalHeights;
      this.$store.dispatch('setColumnHeights', payload);
      if (equalHeights) {
        this.$vnode.context.bindPlugin();
      } else {
        this.$vnode.context.unBindPlugin();
      }
    },
    helperWidth(n) {
      let display = '';
      if (n !== '') {
        display = _.toNumber(n.split('%')[0]).toFixed(1);
        display = `${display}%`;
      }
      return display;
    },
    notLast(i) {
      return (i + 1) < this.droplet.columns.length;
    },
    startResizeDrag(e, i) {
      const cols = this.droplet.columns;
      this.startX = 0;
      this.col = i;
      this.colW = this.getPercWidth(cols[this.col].mediaQueries[0].desktop, cols);
      this.nxtCol = i + 1;
      this.nxtColW = this.getPercWidth(cols[this.nxtCol].mediaQueries[0].desktop, cols);
      e.dataTransfer.setData('text/plain', e.target.id);
      document.addEventListener('dragover', this.commenceResizing, false);
    },
    getPercWidth(w, c) {
      if (w === 'default') {
        w = 100 / c.length;
      }
      return w;
    },
    commenceResizing(e) {
      const colW = this.colW;
      const nxtColW = this.nxtColW;
      const cursor = e.clientX;
      const diff = 0.5;
      if (cursor > 0) {
        if (cursor < this.startX) {
          if (colW >= 10) {
            this.colW = colW - diff;
            this.nxtColW = nxtColW + diff;
          }
        } else if (cursor > this.startX) {
          if (colW <= 90) {
            this.colW = colW + diff;
            this.nxtColW = nxtColW - diff;
          }
        }
        this.startX = event.clientX;
      }
    },
    endResizeDrag() {
      // send new col info
      const payload = {};
      payload.drp = this.droplet;
      payload.colIndex = this.col;
      payload.nextColIndex = this.nxtCol;
      payload.width = this.colW;
      payload.nextColWidth = this.nxtColW;
      this.$store.dispatch('setColumnWidth', payload);
      // reset locals so style tags can take over
      const cols = this.droplet.columns;
      this.colW = this.getPercWidth(cols[this.col].mediaQueries[0].desktop, cols);
      this.nxtColW = this.getPercWidth(cols[this.nxtCol].mediaQueries[0].desktop, cols);
      document.removeEventListener('dragover', this.commenceResizing, false);
      this.$emit('css-generator');
    },
    resetWidths() {
      this.col = null;
      this.colW = 'default';
      this.nxtCol = null;
      this.nxtColW = 'default';
      const cols = this.droplet.columns;
      const payload = {};
      payload.drp = this.droplet;
      _.forEach(cols, (col, index) => {
        payload.colIndex = index;
        payload.width = 'default';
        this.$store.dispatch('setColumnWidth', payload);
      });
      this.$emit('css-generator');
    },
    addColumn() {
      this.resetWidths();
      const total = this.droplet.columns.length;
      if (total <= 5) {
        const newCol = colShape()();
        const payload = {};
        payload.drp = this.droplet;
        payload.newCol = newCol;
        this.promise(
          this.$store.dispatch('addNewColumn', payload),
          'adding column',
        );
        this.$emit('css-generator');
      }
    },
    removeCol(i) {
      this.resetWidths();
      const total = this.droplet.columns.length;
      if (total > this.minLimit) {
        const payload = {};
        payload.drp = this.droplet;
        payload.colIndex = i;
        this.promise(
          this.$store.dispatch('removeColumn', payload),
          'removing column',
        );
        this.$emit('css-generator');
      }
    },
    drzRevertData() {
      const cols = this.droplet.columns;
      if (!_.isNull(this.col)) {
        this.colW = this.getPercWidth(cols[this.col].mediaQueries[0].desktop, cols);
      }
      if (!_.isNull(this.nxtCol)) {
        this.nxtColW = this.getPercWidth(cols[this.nxtCol].mediaQueries[0].desktop, cols);
      }
      if (this.droplet.equalHeights) {
        this.$vnode.context.bindPlugin();
      } else {
        this.$vnode.context.unBindPlugin();
      }
      this.$emit('css-generator');
    },
    setLocals(v) {
      this.$emit('set-display', v);
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
    promise(action, error) {
      const promise = new Promise((resolve, reject) => {
        if (action) {
          resolve();
        } else {
          reject(Error(`Error in columns: ${error}`));
        }
      });
      promise.then(() => {
        if (this.droplet.equalHeights) {
          this.$vnode.context.bindPlugin();
        }
      });
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/columns/components/_edit-options/index.js